import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useEffect, useMemo, useState } from "react";
import { RedoFilterDropdownAnchor } from "../arbiter-components/filter-group/redo-filter-group";
import { RedoListItem } from "../arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "../arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "../flex";
import { Text } from "../text";

function optionToItem<T>(
  option: T,
  optionToFriendlyName: (option: T) => string,
): RedoListItem<T> {
  return {
    id: `${option}`,
    value: option,
    type: "text",
    text: optionToFriendlyName(option),
  };
}

export const SimpleMultiSelectFilterDropdown = genericMemo(
  function SimpleMultiSelectFilterDropdown<T extends string>({
    values,
    setValues,
    options,
    optionToFriendlyName,
    selectedOptionsToDisplay,
    openOnRender,
    filterStyle = "value",
    inputStyleAnchor = false,
    searchPlaceholder,
  }: {
    values: T[] | undefined;
    setValues(items: T[]): void;
    options: T[];
    optionToFriendlyName: (option: T) => string;
    selectedOptionsToDisplay: (selectedOptions: T[] | undefined) => string;
    openOnRender?: boolean;
    filterStyle?: "query" | "value";
    /**
     * Render the trigger as a bordered select-input (label + chevron) instead
     * of the borderless filter-bar chip. Use when the control stands alone as
     * a form field rather than sitting in a filter group.
     */
    inputStyleAnchor?: boolean;
    searchPlaceholder?: string;
  }) {
    const [searchString, setSearchString] = useState<string>("");

    const optionItems: RedoListItem<T>[] = useMemo(() => {
      if (!searchString) {
        return options.map((option) =>
          optionToItem(option, optionToFriendlyName),
        );
      }

      const lowerCaseSearch = searchString.toLowerCase();
      return options
        .filter((option) =>
          optionToFriendlyName(option).toLowerCase().includes(lowerCaseSearch),
        )
        .map((option) => optionToItem(option, optionToFriendlyName));
    }, [options, optionToFriendlyName, searchString]);

    const selectedOptions = useMemo(() => {
      return (values || []).map((value) =>
        optionToItem(value, optionToFriendlyName),
      );
    }, [optionToFriendlyName, values]);

    const [dropdownButtonRef, setDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);

    const [dropdownOpen, setDropdownOpen] = useState(false);

    useEffect(() => {
      if (openOnRender && dropdownButtonRef) {
        dropdownButtonRef.click();
      }
    }, [openOnRender, dropdownButtonRef]);

    const handleDropdownOpenChange = useHandler((open: boolean) => {
      setDropdownOpen(open);
      if (!open) {
        setSearchString("");
      }
    });

    const selectOptions = useHandler((items: RedoListItem<T>[]) =>
      setValues(items.map((i) => i.value)),
    );

    const anyItemsSelected = values && values.length > 0;

    const displayText = selectedOptionsToDisplay(values);
    // The input-style trigger shows the same summary string the chip does; a
    // selected value renders as primary text, the empty default as placeholder.
    const displayItem: RedoListItem<T> | undefined =
      values && values.length > 0
        ? {
            id: "__display__",
            type: "text",
            value: values[0]!,
            text: displayText,
          }
        : undefined;

    return (
      <>
        {inputStyleAnchor ? (
          <RedoSingleSelectDropdownInputButton<T>
            isDropdownOpen={dropdownOpen}
            onClick={() => handleDropdownOpenChange(!dropdownOpen)}
            placeholder={displayText}
            ref={setDropdownButtonRef}
            selectedItem={displayItem}
            size="xs"
          />
        ) : (
          <RedoFilterDropdownAnchor
            color={filterStyle === "query" ? "secondary" : "primary"}
            onClick={() => handleDropdownOpenChange(!dropdownOpen)}
            ref={setDropdownButtonRef}
            text={displayText}
            tooltip={
              anyItemsSelected && (
                <Flex dir="column">
                  {values.map((value) => (
                    <Text key={value}>{optionToFriendlyName(value)}</Text>
                  ))}
                </Flex>
              )
            }
            weight={filterStyle === "query" ? "regular" : "medium"}
          />
        )}
        <RedoMultiselectDropdown<T>
          dropdownAnchor={dropdownButtonRef}
          dropdownOpen={dropdownOpen}
          fitToAnchor={false}
          items={optionItems}
          searchPlaceholder={searchPlaceholder || "Search options..."}
          searchString={searchString}
          selectedItems={selectedOptions}
          setDropdownOpen={handleDropdownOpenChange}
          setSearchString={setSearchString}
          setSelectedItems={selectOptions}
          size="xs"
        />
      </>
    );
  },
);
